/**
 * The Sort menu, as data — one list of four orderings for both mail lanes.
 *
 * The design draws the same menu on the personal and the unified inbox, but the
 * two endpoints speak different wire vocabularies: the personal list takes
 * `sort_by` ∈ `comm_date | subject | from_email | unread`, the unified one takes
 * `sort` ∈ `received_at | subject | sender_email | recipient_display_name |
 * replied | unread`. Those are genuinely different APIs over genuinely different
 * pipelines, and merging them would mean one of the two lists sorting on a field
 * it does not have.
 *
 * So what is shared is the MENU — the four labels, their order, and which one is
 * lit — and each lane keeps a small adapter to and from its own parameters.
 * A fifth ordering is a row here plus one line in each adapter; it cannot be
 * added to one lane and forgotten on the other, because `optionFor` resolves
 * the active key through the same table that renders the list.
 *
 * "Sender A–Z" carries an EN DASH, as the design writes it.
 */

export const SORT_OPTIONS = [
    { key: 'newest', label: 'Newest first' },
    { key: 'oldest', label: 'Oldest first' },
    { key: 'unread', label: 'Unread first' },
    { key: 'sender', label: 'Sender A–Z' },
];

export const DEFAULT_SORT = 'newest';

/**
 * The personal list's `sort_by` / `direction` pair.
 *
 * `unread` pins `desc` because the backend's composite key already encodes
 * "unread before read, newest within" as one descending value — see
 * `services/mail_sorting.py`. Sending `asc` would be asking for an ordering the
 * menu does not offer.
 */
const PERSONAL_PATCHES = {
    newest: { sortBy: 'comm_date', direction: 'desc' },
    oldest: { sortBy: 'comm_date', direction: 'asc' },
    unread: { sortBy: 'unread', direction: 'desc' },
    sender: { sortBy: 'from_email', direction: 'asc' },
};

/** The unified list's `sort` / `direction` pair. Same four orderings. */
const UNIFIED_PATCHES = {
    newest: { sort: 'received_at', direction: 'desc' },
    oldest: { sort: 'received_at', direction: 'asc' },
    unread: { sort: 'unread', direction: 'desc' },
    sender: { sort: 'sender_email', direction: 'asc' },
};

function adapter(patches, read) {
    return {
        /** The filter patch for an option key. */
        patch: (key) => patches[key] || patches[DEFAULT_SORT],
        /**
         * Which option the current filters represent.
         *
         * Falls back to the default rather than to "nothing selected": these
         * lists are always sorted by something, so a menu with no check mark
         * would be claiming otherwise. A filter combination outside the table
         * (reachable by hand-editing the URL) reads as Newest, which is what
         * the server does with an unrecognised value anyway.
         */
        activeKey: (filters = {}) => read(filters) || DEFAULT_SORT,
    };
}

export const personalSort = adapter(PERSONAL_PATCHES, (filters) => {
    const field = filters.sortBy || 'comm_date';
    const direction = filters.direction === 'asc' ? 'asc' : 'desc';
    if (field === 'unread') return 'unread';
    if (field === 'from_email') return 'sender';
    if (field === 'comm_date') return direction === 'asc' ? 'oldest' : 'newest';
    return null;
});

export const unifiedSort = adapter(UNIFIED_PATCHES, (filters) => {
    const field = filters.sort || 'received_at';
    const direction = filters.direction === 'asc' ? 'asc' : 'desc';
    if (field === 'unread') return 'unread';
    if (field === 'sender_email') return 'sender';
    if (field === 'received_at') return direction === 'asc' ? 'oldest' : 'newest';
    return null;
});

/** The option row for a key, for a label or a tooltip. */
export function optionFor(key) {
    return SORT_OPTIONS.find((o) => o.key === key) || SORT_OPTIONS[0];
}
